import { Injectable, NgZone, OnDestroy } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { AuthenticationService } from '@common/services';
import { InsightsService } from '@core/services';

import { CustomSwalService } from 'app/helpers';

import { environment } from 'environments/environment';

import { AgentAuthService } from './agent-auth.service';
import { ChatAgentService } from './chat-agent.service';
import { ChatService } from './chat.service';
import { ChatHistoryService } from './chat-history.service';
import { AppAction, AppError, ChatFeedback, ChatMessage, ChatSummary, DisplayCitation, IAnnotation, StoredChat } from '../types';
import { createAppError, DoneChime, FileConversionResult } from '../utils';

/**
 * Aviso que reciben las vistas cada vez que cambia la conversación.
 */
export interface ChatSessionChange {
  /** El cambio agregó contenido al final del hilo y conviene bajar el scroll */
  scroll: boolean;
}

/**
 * Dueño de la conversación activa con Bryant.
 *
 * El hilo, el estado del turno y el ciclo que consume el stream viven acá y no
 * en `ChatContentComponent`: así la respuesta sigue llegando aunque el usuario
 * cierre el panel o navegue a otra pantalla, y el dock y la página completa
 * muestran la misma conversación. Las vistas solo se suscriben a `changes$` y
 * le piden acciones a este servicio.
 *
 * Lo único que no sobrevive es la recarga o el cierre de la pestaña, porque la
 * petición muere con el documento: para eso se avisa antes de salir.
 */
@Injectable({ providedIn: 'root' })
export class ChatSessionService implements OnDestroy {

  // ─── Conversación ───────────────────────────────────────────────────

  /** Hilo completo de la conversación */
  public messages: ChatMessage[] = [];
  /** Conversación viva en el agente, la devuelve el propio stream */
  public conversationId: string | null = null;
  /** Sesión que el agente arrastra entre turnos */
  public session: string | null = null;
  /** Estado del turno actual */
  public status: 'idle' | 'sending' | 'streaming' | 'error' = 'idle';
  /** Error estructurado del último turno, si lo hubo */
  public error: AppError | null = null;
  /** Archivos convertidos a data uri, pendientes de enviarse con el próximo mensaje */
  public pendingFiles: FileConversionResult[] = [];

  // ─── Historial ──────────────────────────────────────────────────────

  /** Conversaciones guardadas del usuario, de la más reciente a la más antigua */
  public history: ChatSummary[] = [];
  /** Conversación abierta en este momento, se corresponde con una llave del storage */
  public activeChatId: string | null = null;
  /** Se está leyendo el índice o un hilo del storage */
  public historyLoading: boolean = false;

  /** Correo del usuario, es la primera parte de la llave del storage */
  public email: string = '';

  /** Fecha de creación de la conversación abierta, se conserva al guardar */
  private activeChatCreatedAt: string | null = null;
  /** Nombre de la conversación abierta. Vacío hasta el primer mensaje */
  private activeChatTitle: string = '';
  /** Escrituras encoladas contra el storage, para que no se pisen entre sí */
  private saveQueue: Promise<void> = Promise.resolve();

  // ─── Avisos hacia las vistas ────────────────────────────────────────

  private _changes = new Subject<ChatSessionChange>();
  private _busy = new BehaviorSubject<boolean>(false);
  private _unread = new BehaviorSubject<boolean>(false);

  /** Vistas del chat montadas en este momento */
  private viewers = 0;

  /** Sonido que avisa que Bryant terminó de responder */
  private readonly doneChime = new DoneChime();
  /** El turno en curso llegó a CHAT_DONE (no se cortó ni falló): suena el aviso */
  private turnCompleted = false;

  /** La salida ya la confirmó el usuario en el aviso propio, no hay que volver a frenarla */
  private allowUnload = false;

  private readonly onBeforeUnload = (event: BeforeUnloadEvent) => this.guardUnload(event);
  private readonly onKeydown = (event: KeyboardEvent) => this.guardReloadShortcut(event);

  /**
   * Constructor
   * @param {ChatService} _chatService Servicio de streaming contra el agente Bryant
   * @param {AgentAuthService} _agentAuthService Provee el access token del agente Bryant
   * @param {ChatAgentService} _agentService Despliegue del agente contra el que se habla
   * @param {ChatHistoryService} _historyService Historial de conversaciones en el blob storage
   * @param {AuthenticationService} _authenticationService Usuario con el que se etiquetan las trazas
   * @param {InsightsService} _insightsService
   * @param {CustomSwalService} _swalService
   * @param {TranslateService} _translateService
   * @param {NgZone} _zone
   */
  constructor(
    private _chatService: ChatService,
    private _agentAuthService: AgentAuthService,
    private _agentService: ChatAgentService,
    private _historyService: ChatHistoryService,
    private _authenticationService: AuthenticationService,
    private _insightsService: InsightsService,
    private _swalService: CustomSwalService,
    private _translateService: TranslateService,
    private _zone: NgZone
  ) {
    // Se escuchan fuera de Angular: se disparan con cada tecla y no tocan la
    // vista salvo cuando hay que mostrar el aviso
    this._zone.runOutsideAngular(() => {
      window.addEventListener('beforeunload', this.onBeforeUnload);
      window.addEventListener('keydown', this.onKeydown);
    });
  }

  /**
   * OnDestroy
   */
  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeydown);
  }

  // ─── Observables ────────────────────────────────────────────────────

  /** Emite cada vez que cambia algo que las vistas tienen que repintar */
  get changes$(): Observable<ChatSessionChange> { return this._changes.asObservable(); }

  /** Indica si hay un turno en curso */
  get busy$(): Observable<boolean> { return this._busy.asObservable(); }

  /** Indica si llegó una respuesta mientras no había ninguna vista del chat abierta */
  get unread$(): Observable<boolean> { return this._unread.asObservable(); }

  /**
   * Indica si hay un turno en curso.
   */
  get isBusy(): boolean {
    return this.status === 'sending' || this.status === 'streaming';
  }

  /**
   * Mensaje que está recibiendo tokens en este momento.
   */
  get streamingMessage(): ChatMessage | undefined {
    return this.messages.find(m => m.isStreaming);
  }

  // ─── Vistas ─────────────────────────────────────────────────────────

  /**
   * Registra una vista del chat. Mientras haya alguna, lo que llegue ya se ve
   * y no queda pendiente de leer.
   */
  attach(): void {
    this.viewers++;
    this._unread.next(false);
  }

  /**
   * Da de baja una vista del chat. No corta el stream: la respuesta sigue
   * llegando y queda en el hilo para la próxima vista que se abra.
   */
  detach(): void {
    this.viewers = Math.max(0, this.viewers - 1);
  }

  /**
   * Asocia la sesión al usuario y carga su historial. Si cambió el usuario sin
   * recargar la página, la conversación del anterior no se le muestra al nuevo.
   *
   * @param {string} email Correo del usuario, llave del historial
   */
  connect(email: string): void {
    if (!email || email === this.email) return;

    if (this.email) {
      this._chatService.cancelStream();
      this.apply(this._chatService.clearChat());
      this.pendingFiles = [];
      this.history = [];
      this.resetActiveChat();
    }

    this.email = email;
    this.restore();
  }

  // ─── Envío y streaming ──────────────────────────────────────────────

  /**
   * Envía el mensaje al agente y va aplicando las acciones que emite el stream.
   * @param {string} message Texto del mensaje que escribe el usuario
   */
  async sendMessage(message: string): Promise<void> {
    // Un turno a la vez: mientras se está respondiendo el compositor queda bloqueado
    if (this.isBusy) return;

    // El token se pide antes de tocar el estado: si falla, el mensaje y los
    // adjuntos siguen intactos y el usuario puede reintentar
    const token = await this.requestToken();
    if (!token) return;

    const files = this.pendingFiles;
    this.pendingFiles = [];
    this.error = null;
    this.status = 'sending';
    this.startTurn();
    this.notify(true);

    this.log(`Envió mensaje: ${message}`, 'null');

    try {
      const stream = this._chatService.sendMessage(
        token,
        message,
        this.conversationId,
        this.session,
        files.length > 0 ? files : undefined
      );

      for await (const action of stream) {
        this.apply(action);
        this.notify(true);
      }

      this.log(`Recibió respuesta de Bryant`, message);
    } catch (err) {
      // El generador ya emitió CHAT_ERROR antes de propagar, así que la vista
      // ya quedó en estado de error: acá solo queda dejar rastro del fallo
      this.log(`Error al recibir respuesta`, message, err, true);
    } finally {
      this.finishTurn();
    }
  }

  /**
   * Responde a una solicitud de aprobación de una herramienta MCP y retoma el stream.
   * @param {ChatMessage} item Mensaje que contiene la solicitud
   * @param {boolean} approved Indica si el usuario autorizó la ejecución
   */
  async respondApproval(item: ChatMessage, approved: boolean): Promise<void> {
    if (!item.mcpApproval || item.approvalResolved) return;

    const token = await this.requestToken();
    if (!token) return;

    item.approvalResolved = true;
    this.error = null;
    this.status = 'sending';
    this.startTurn();
    this.notify(true);

    try {
      const stream = this._chatService.sendMcpApproval(
        token,
        item.mcpApproval.id,
        approved,
        item.mcpApproval.previousResponseId ?? this.conversationId ?? '',
        this.conversationId ?? ''
      );

      for await (const action of stream) {
        this.apply(action);
        this.notify(true);
      }
    } catch (err) {
      this.log(`Error al responder la aprobación de una herramienta`, item.mcpApproval.toolName, err, true);
    } finally {
      this.finishTurn();
    }
  }

  /**
   * Corta la respuesta que se está escribiendo.
   */
  stopStreaming(): void {
    const action = this._chatService.cancelStream();

    if (action) {
      this.apply(action);
      this.notify();
    }
  }

  /**
   * Reintenta la acción de recuperación que trae el error, si la hay.
   */
  retry(): void {
    const handler = this.error?.action?.handler;

    this.error = null;
    this.status = 'idle';
    this.notify();

    if (handler) {
      handler();
    }
  }

  /**
   * Descarta el error sin reintentar.
   */
  dismissError(): void {
    this.apply(this._chatService.clearError());
    this.status = 'idle';
    this.notify();
  }

  /**
   * Pide el access token del agente y deja el error a la vista si no se pudo
   * obtener, que en la práctica significa que la sesión de Entra ya no sirve.
   *
   * @returns {Promise<string>} Devuelve el token, o null si no se pudo obtener
   */
  private async requestToken(): Promise<string> {
    try {
      return await this._agentAuthService.getAccessToken();
    } catch (err) {
      this.apply({ type: 'CHAT_ERROR', error: createAppError(err, 'AUTH') });
      this.notify();

      this.log(`Error al obtener el token del agente`, '', err, true);

      return null;
    }
  }

  /**
   * Abre un turno. Se llama mientras sigue vivo el gesto del usuario (enviar o
   * aprobar), que es lo que deja al navegador reproducir el aviso al final.
   */
  private startTurn(): void {
    this.turnCompleted = false;
    this.doneChime.unlock();
  }

  /**
   * Deja el turno cerrado y guarda el hilo.
   */
  private finishTurn(): void {
    this.messages.forEach(m => (m.isStreaming = false));
    this.status = this.error ? 'error' : 'idle';
    this.persist();

    // Sólo cuando la respuesta terminó bien: cortarla a mano o un error no suenan
    if (this.turnCompleted && !this.error) {
      this.doneChime.play();
    }

    // Si nadie estaba mirando, la burbuja avisa que la respuesta ya está
    if (this.viewers === 0) {
      this._unread.next(true);
      this.log(`Recibió la respuesta con el chat cerrado`, this.conversationId ?? '');
    }

    this.notify(true);
  }

  // ─── Reductor de acciones ───────────────────────────────────────────

  /**
   * Aplica sobre el hilo una acción emitida por el servicio.
   * @param {AppAction} action Acción del stream
   */
  private apply(action: AppAction): void {
    switch (action.type) {

      case 'CHAT_SEND_MESSAGE':
        this.messages.push({ ...action.message, display: action.message.content });
        break;

      case 'CHAT_ADD_ASSISTANT_MESSAGE':
        this.messages.push({
          id: action.messageId,
          role: 'assistant',
          content: '',
          display: '',
          isStreaming: true,
          question: this.lastUserMessage
        });
        this.status = 'streaming';
        break;

      case 'CHAT_START_STREAM':
        if (action.conversationId) {
          this.conversationId = action.conversationId;
        }
        this.status = 'streaming';
        break;

      case 'CHAT_STREAM_CHUNK': {
        const item = this.find(action.messageId);
        if (item) {
          item.content += action.content ?? '';
          this.render(item);
        }
        break;
      }

      case 'CHAT_STREAM_REASONING':
        this.applyReasoning(action);
        break;

      case 'CHAT_STREAM_ANNOTATIONS': {
        const item = this.find(action.messageId);
        if (item) {
          item.annotations = [...(item.annotations ?? []), ...action.annotations];
          this.render(item);
        }
        break;
      }

      case 'CHAT_MCP_APPROVAL_REQUEST':
        this.messages.push({
          id: `${action.messageId}-approval`,
          role: 'approval',
          content: '',
          mcpApproval: {
            ...action.approvalRequest,
            previousResponseId: action.previousResponseId ?? action.approvalRequest.previousResponseId
          }
        });
        break;

      case 'CHAT_STREAM_COMPLETE': {
        const item = this.streamingMessage;
        if (item) {
          item.more = { ...(item.more ?? {}), usage: action.usage, time: new Date().toISOString() };
          item.duration = action.usage?.duration;
        }
        break;
      }

      case 'CHAT_SESSION':
        this.session = action.session;
        break;

      case 'CHAT_STREAM_CHART_BLUEPRINT': {
        const item = this.find(action.messageId);
        if (item) {
          item.charts = [...(item.charts ?? []), action.cardJson];
        }
        break;
      }

      case 'CHAT_DONE':
      case 'CHAT_CANCEL_STREAM':
        this.turnCompleted = action.type === 'CHAT_DONE';
        this.messages.forEach(m => (m.isStreaming = false));
        this.status = 'idle';
        break;

      case 'CHAT_ERROR':
        this.error = action.error;
        this.status = 'error';
        // Una burbuja vacía del asistente no aporta nada al lado del aviso de error
        this.messages = this.messages.filter(m => !(m.role === 'assistant' && !m.content));
        break;

      case 'CHAT_CLEAR_ERROR':
        this.error = null;
        break;

      case 'CHAT_CLEAR':
        this.messages = [];
        this.conversationId = null;
        this.session = null;
        this.error = null;
        this.status = 'idle';
        break;

      case 'AUTH_TOKEN_EXPIRED':
        this._swalService.showWarningMsg(
          this.label('Tu sesión expiró. Vuelve a iniciar sesión para seguir conversando.',
            'Your session expired. Sign in again to keep chatting.')
        );
        break;
    }
  }

  /**
   * Traduce un evento de razonamiento del agente en un paso de la lista.
   * @param action Acción de razonamiento del stream
   */
  private applyReasoning(action: Extract<AppAction, { type: 'CHAT_STREAM_REASONING' }>): void {
    const item = this.find(action.messageId);
    if (!item) return;

    item.reasoning = item.reasoning ?? [];

    // El agente identifica cada paso con el id de la llamada a la herramienta,
    // así el evento de cierre encuentra el paso que abrió
    const step = item.reasoning.find(s => s.callToolId && s.callToolId === action.callToolId);

    if (action.status === 'end') {
      if (step) {
        step.done = true;
        if (action.content) step.desc = action.content;
      }
      return;
    }

    if (step) {
      step.desc = action.content ?? step.desc;
      return;
    }

    item.reasoning.push({
      type: action.tool ? 'action' : 'plan',
      title: action.tool ?? this.label('Analizando', 'Thinking'),
      desc: action.content ?? '',
      tool: action.tool,
      callToolId: action.callToolId,
      done: false
    });
  }

  // ─── Citas y render del contenido ───────────────────────────────────

  /**
   * Recalcula el texto que se muestra: numera las citas del agente y reemplaza
   * en el markdown el marcador que trae cada una.
   * @param {ChatMessage} item Mensaje a renderizar
   */
  private render(item: ChatMessage): void {
    const annotations = item.annotations ?? [];
    let text = item.content ?? '';

    if (annotations.length === 0) {
      item.citations = [];
      item.display = text;
      return;
    }

    const citations: DisplayCitation[] = [];

    annotations.forEach(annotation => {
      // Una misma fuente puede citarse varias veces: se numera una sola vez
      let citation = citations.find(c => this.sameSource(c, annotation));

      if (!citation) {
        citation = {
          index: citations.length + 1,
          label: annotation.label,
          url: annotation.url,
          fileId: annotation.fileId,
          quote: annotation.quote
        };
        citations.push(citation);
      }

      if (annotation.textToReplace) {
        const marker = annotation.url
          ? ` [<sup>${citation.index}</sup>](${annotation.url})`
          : ` <sup class="bl-cite">${citation.index}</sup>`;

        text = text.split(annotation.textToReplace).join(marker);
      }
    });

    item.citations = citations;
    item.display = text;
  }

  /**
   * Indica si una anotación apunta a la misma fuente que una cita ya numerada.
   * @param {DisplayCitation} citation Cita ya registrada
   * @param {IAnnotation} annotation Anotación nueva
   * @returns {boolean} Devuelve true cuando ambas señalan el mismo documento o url
   */
  private sameSource(citation: DisplayCitation, annotation: IAnnotation): boolean {
    if (annotation.url || citation.url) return citation.url === annotation.url;
    if (annotation.fileId || citation.fileId) return citation.fileId === annotation.fileId;

    return citation.label === annotation.label;
  }

  // ─── Feedback ───────────────────────────────────────────────────────

  /**
   * Manda la valoración al backend del agente.
   * @param {ChatMessage} item Mensaje valorado
   * @param {'like' | 'dislike'} reaction Valoración elegida
   * @param {string} text Comentario del usuario
   */
  async saveFeedback(item: ChatMessage, reaction: 'like' | 'dislike', text: string): Promise<void> {
    const user = this._authenticationService.currentUserValue;

    const feedback: ChatFeedback = {
      conversationId: this.conversationId ?? '',
      messageId: item.id,
      userId: user?.id,
      userName: user?.fullname ?? '',
      reaction,
      question: item.question,
      answer: item.content,
      feedbackText: text
    };

    try {
      const token = await this._agentAuthService.getAccessToken();
      await this._chatService.saveChatFeedback(feedback, token);

      item.feedbackSent = true;
      this.persist();
    } catch (error) {
      item.feedback = undefined;
      this.log(`Error al guardar el feedback`, item.id, error, true);
    } finally {
      this.notify();
    }
  }

  // ─── Conversación ───────────────────────────────────────────────────

  /**
   * Borra la conversación: la saca de la vista, del historial y del agente.
   */
  async clearChat(): Promise<void> {
    this.log(`Limpio el Chat`, { messages: this.messages.length, conversationId: this.conversationId });

    const conversationId = this.conversationId;
    const chatId = this.activeChatId;

    this._chatService.cancelStream();
    this.apply(this._chatService.clearChat());
    this.pendingFiles = [];
    this.resetActiveChat();
    this.notify();

    // El hilo guardado también se da de baja: la papelera borra, no archiva
    if (chatId && this.email) {
      this.queue(async () => {
        this.history = await this._historyService.remove(this.email, chatId);
        this.notify();
      });
    }

    // El hilo también vive del lado del agente, así que hay que darlo de baja
    if (conversationId) {
      try {
        const token = await this._agentAuthService.getAccessToken();
        await this._chatService.deleteConversation(conversationId, token);
      } catch (error) {
        this.log(`Error al eliminar la conversación`, conversationId, error, true);
      }
    }
  }

  /**
   * Empieza una conversación nueva. La anterior no se pierde: ya quedó
   * guardada en el historial y se puede volver a abrir desde el panel.
   */
  newChat(): void {
    if (this.isBusy) return;

    this._chatService.cancelStream();
    this.apply(this._chatService.clearChat());
    this.pendingFiles = [];
    this.resetActiveChat();
    this.notify();

    if (this.email) {
      this.queue(() => this._historyService.setLastActive(this.email, null));
    }
  }

  /**
   * Cambia el despliegue del agente. La conversación abierta vive en el
   * anterior, así que se sigue en una nueva; la otra queda en el historial.
   *
   * @param {string} key Llave del despliegue (ver ChatAgentService)
   */
  switchAgent(key: string): void {
    if (this.isBusy) return;

    const from = this._agentService.current.key;
    if (!this._agentService.select(key)) return;

    this.log(`Cambió el agente del chat`, { from, to: key });

    if (this.messages.length > 0) {
      this.newChat();
      return;
    }

    this.notify();
  }

  /**
   * Abre una conversación guardada. Si se tuvo con otro despliegue del agente,
   * se cambia a ese: el hilo del lado del agente vive ahí.
   *
   * @param {string} id Uuid de la conversación
   * @param {boolean} log Indica si hay que dejar rastro de la apertura
   * @param {boolean} followAgent Cambia de agente si el hilo es de otro. Si no, no lo abre
   */
  async openChat(id: string, log: boolean = true, followAgent: boolean = true): Promise<void> {
    if (!this.email || this.isBusy || !id) return;
    if (id === this.activeChatId) return;

    this.historyLoading = true;
    this.notify();

    try {
      const chat = await this._historyService.load(this.email, id);

      // La entrada quedó en el índice pero el hilo ya no está: se limpia
      if (!chat) {
        this.history = await this._historyService.remove(this.email, id);
        return;
      }

      if (chat.agent && chat.agent !== this._agentService.current.key) {
        if (!followAgent) return;
        this._agentService.select(chat.agent);
      }

      this._chatService.cancelStream();

      this.activeChatId = chat.id;
      this.activeChatTitle = chat.title ?? '';
      this.activeChatCreatedAt = chat.createdAt ?? new Date().toISOString();
      this.conversationId = chat.conversationId ?? null;
      this.session = chat.session ?? null;
      this.messages = (chat.messages ?? []) as ChatMessage[];
      this.messages.forEach(m => this.render(m));
      this.pendingFiles = [];
      this.error = null;
      this.status = 'idle';

      await this._historyService.setLastActive(this.email, chat.id);

      if (log) {
        this.log(`Abrió una conversación del historial`, chat.title);
      }
    } catch (error) {
      this.log(`Error al abrir una conversación del historial`, id, error, true);
    } finally {
      this.historyLoading = false;
      this.notify(true);
    }
  }

  /**
   * Elimina una conversación del historial. La confirmación la pide la vista.
   * @param {ChatSummary} chat Conversación a eliminar
   */
  async deleteChat(chat: ChatSummary): Promise<void> {
    if (!this.email || !chat?.id) return;

    try {
      this.history = await this._historyService.remove(this.email, chat.id);

      // Si era la que estaba abierta, la vista queda en una conversación nueva
      if (chat.id === this.activeChatId) {
        this._chatService.cancelStream();
        this.apply(this._chatService.clearChat());
        this.resetActiveChat();
      }

      this.log(`Eliminó una conversación del historial`, chat.id);
    } catch (error) {
      this.log(`Error al eliminar una conversación del historial`, chat.id, error, true);
    } finally {
      this.notify();
    }
  }

  /**
   * Guarda el nombre nuevo de una conversación.
   * @param {ChatSummary} chat Conversación renombrada
   * @param {string} title Nombre nuevo
   */
  async renameChat(chat: ChatSummary, title: string): Promise<void> {
    if (!this.email || !chat?.id || !title) return;

    try {
      this.history = await this._historyService.rename(this.email, chat.id, title);

      if (chat.id === this.activeChatId) {
        this.activeChatTitle = title;
      }
    } catch (error) {
      this.log(`Error al renombrar una conversación`, chat.id, error, true);
    } finally {
      this.notify();
    }
  }

  /**
   * Guarda el hilo en el blob storage, bajo la llave `email/uuid`.
   */
  private persist(): void {
    // Sin correo no hay llave con la que guardar, y un hilo vacío no aporta
    // nada al historial
    if (!this.email || this.messages.length === 0) return;

    const now = new Date().toISOString();

    this.activeChatId = this.activeChatId ?? this._historyService.newId();
    this.activeChatCreatedAt = this.activeChatCreatedAt ?? now;
    // El nombre se deduce del primer mensaje, salvo que el usuario lo haya cambiado
    this.activeChatTitle = this.activeChatTitle
      || this._historyService.titleFrom(this.messages)
      || this.label('Conversación sin título', 'Untitled conversation');

    const chat: StoredChat = {
      id: this.activeChatId,
      title: this.activeChatTitle,
      createdAt: this.activeChatCreatedAt,
      updatedAt: now,
      conversationId: this.conversationId,
      session: this.session,
      agent: this._agentService.current.key,
      // El razonamiento y los adjuntos en base64 no se guardan: pesan mucho y
      // solo tienen sentido durante el turno en el que se generaron
      messages: this.messages.map(m => ({
        ...m,
        reasoning: undefined,
        reasoningOpen: false,
        isStreaming: false,
        attachments: m.attachments?.map(a => ({ ...a, dataUri: undefined }))
      }))
    };

    this.queue(async () => {
      this.history = await this._historyService.save(this.email, chat);
      this.notify();
    });
  }

  /**
   * Carga el índice del usuario y reabre la última conversación.
   */
  private async restore(): Promise<void> {
    this.historyLoading = true;
    this.notify();

    try {
      const index = await this._historyService.index(this.email);

      this.history = index.chats;

      // Si el usuario ya venía escribiendo en este hilo no se pisa lo que tiene.
      // Tampoco se le cambia el agente que eligió: si el hilo es de otro, arranca en blanco
      if (index.lastActiveId && this.messages.length === 0) {
        await this.openChat(index.lastActiveId, false, false);
      }
    } catch (error) {
      this.log(`Error al cargar el historial de conversaciones`, this.email, error, true);
    } finally {
      this.historyLoading = false;
      this.notify();
    }
  }

  /**
   * Deja la conversación nueva, sin llave asignada todavía.
   */
  private resetActiveChat(): void {
    this.activeChatId = null;
    this.activeChatCreatedAt = null;
    this.activeChatTitle = '';
  }

  /**
   * Encola una escritura sobre el storage. El índice se lee y se vuelve a
   * escribir completo, así que dos guardados en paralelo se pisarían.
   *
   * @param {() => Promise<void>} work Escritura a encolar
   */
  private queue(work: () => Promise<void>): void {
    this.saveQueue = this.saveQueue
      .then(work)
      .catch((error: unknown) => {
        this.log(`Error al guardar la conversación`, this.activeChatId ?? '', error, true);
      });
  }

  // ─── Salida de la página ────────────────────────────────────────────

  /**
   * Frena el cierre o la recarga de la pestaña mientras Bryant responde: la
   * petición muere con el documento y la respuesta a medias se pierde.
   *
   * El navegador muestra su propio diálogo y no deja personalizar el texto.
   *
   * @param {BeforeUnloadEvent} event Evento de salida del documento
   */
  private guardUnload(event: BeforeUnloadEvent): string | void {
    if (!this.isBusy || this.allowUnload) return;

    this.log(`Intentó salir de la página con una respuesta en curso`, this.conversationId ?? '');

    event.preventDefault();
    // Chrome y Edge todavía piden el returnValue para mostrar el diálogo
    event.returnValue = '';

    return '';
  }

  /**
   * La recarga por teclado (F5, Ctrl/Cmd+R) sí se puede interceptar antes de
   * que el navegador la ejecute, así que ahí se muestra un aviso propio que
   * explica qué se pierde. El cierre de la pestaña no admite esto y queda en
   * manos de `guardUnload`.
   *
   * @param {KeyboardEvent} event Tecla presionada
   */
  private guardReloadShortcut(event: KeyboardEvent): void {
    if (!this.isBusy) return;

    const isReload = event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key?.toLowerCase() === 'r');

    if (!isReload) return;

    event.preventDefault();

    this._zone.run(() => this.confirmReload(event.shiftKey));
  }

  /**
   * Explica que la respuesta se va a perder y recarga solo si el usuario insiste.
   * @param {boolean} hard El usuario pidió recargar sin caché (Shift)
   */
  private async confirmReload(hard: boolean): Promise<void> {
    const t = (key: string) => this._translateService.instant(key);

    const result = await this._swalService.showQuestion(
      t('BELIVPEDIA.MESSAGES.BIX_LEAVE_TITLE'),
      t('BELIVPEDIA.MESSAGES.BIX_LEAVE_TEXT'),
      t('BELIVPEDIA.MESSAGES.BIX_LEAVE_CONFIRM'),
      t('BELIVPEDIA.MESSAGES.BIX_LEAVE_CANCEL')
    );

    if (!result?.isConfirmed) {
      this.log(`Canceló la recarga para no perder la respuesta en curso`, this.conversationId ?? '');
      return;
    }

    // Si la respuesta terminó mientras el aviso estaba abierto, ya no hay nada que perder
    if (this.isBusy) {
      this.log(`Recargó la página con una respuesta en curso`, this.conversationId ?? '');
    }

    this.allowUnload = true;
    window.location.reload();
  }

  // ─── Utilidades ─────────────────────────────────────────────────────

  /**
   * Avisa a las vistas que cambió la conversación.
   * @param {boolean} scroll El cambio agregó contenido al final del hilo
   */
  private notify(scroll: boolean = false): void {
    if (this._busy.value !== this.isBusy) {
      this._busy.next(this.isBusy);
    }

    this._changes.next({ scroll });
  }

  /**
   * Texto del último mensaje del usuario, se adjunta al feedback.
   */
  private get lastUserMessage(): string {
    return [...this.messages].reverse().find(m => m.role === 'user')?.content ?? '';
  }

  /**
   * Busca un mensaje del hilo por su id.
   * @param {string} messageId Id del mensaje
   * @returns {ChatMessage} Devuelve el mensaje o undefined
   */
  private find(messageId: string): ChatMessage | undefined {
    return this.messages.find(m => m.id === messageId);
  }

  /**
   * Etiqueta localizada sin depender de claves nuevas del backend de traducciones.
   * @param {string} es Texto en español
   * @param {string} en Texto en inglés
   * @returns {string} Texto según el idioma activo
   */
  private label(es: string, en: string): string {
    return this._translateService.currentLang === 'en' ? en : es;
  }

  /**
   * Manda una traza a Application Insights, etiquetada con el proyecto y el
   * usuario que la originó.
   */
  private log(message: string, data: any, ex: any = null, error: boolean = false): void {
    const user = this._authenticationService.currentUserValue;

    if (error) {
      this._insightsService.traceError(environment.projects.Bix, message, data, ex, user);
      return;
    }

    this._insightsService.traceInfo(environment.projects.Bix, message, data, user);
  }
}
